import { useEffect, useState } from "react";
import { createBlock, type AvailabilityBlock } from "@/lib/availability";
import { CALENDAR_TARGETS } from "@/pages/admin/calendar/calendarUtils";

interface BlockDateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (block: AvailabilityBlock) => void;
  defaultDate?: string;
}

const BLOCK_TYPES = [
  { value: "blocked", label: "Blocked" },
  { value: "maintenance", label: "Maintenance" },
  { value: "holiday", label: "Holiday" },
];

export default function BlockDateModal({ open, onClose, onCreated, defaultDate }: BlockDateModalProps) {
  const today = defaultDate || new Date().toISOString().slice(0, 10);
  const [scope, setScope] = useState("farm");
  const [target, setTarget] = useState(CALENDAR_TARGETS.farm[0]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [blockType, setBlockType] = useState("blocked");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStartDate(defaultDate || new Date().toISOString().slice(0, 10));
    setEndDate(defaultDate || new Date().toISOString().slice(0, 10));
    setReason("");
    setError("");
  }, [open, defaultDate]);

  useEffect(() => {
    if (open) setTarget(CALENDAR_TARGETS[scope][0]);
  }, [scope, open]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setError("Please choose a start and end date.");
      return;
    }
    if (endDate < startDate) {
      setError("The end date can't be before the start date.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const block = await createBlock({
        scope,
        target,
        start_date: startDate,
        end_date: endDate,
        block_type: blockType,
        reason: reason.trim() || null,
      });
      onCreated(block);
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't save the block.");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400";
  const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1.5";

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} aria-hidden="true"></div>
      <div
        className="relative w-full max-w-lg bg-background-50 border border-background-200 rounded-lg p-5 md:p-6 max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label="Block dates"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold">Block availability</h2>
            <p className="text-sm text-foreground-600 mt-1">
              Reserve dates so guests can't book them — maintenance, holidays or private events.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {error ? (
          <p className="flex items-start gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-md px-3 py-2.5 mt-4">
            <i className="ri-error-warning-line text-lg"></i>
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <label>
            <span className={labelCls}>Scope</span>
            <select value={scope} onChange={(event) => setScope(event.target.value)} className={field}>
              <option value="farm">Farm</option>
              <option value="experience">Experience</option>
            </select>
          </label>
          <label>
            <span className={labelCls}>Target</span>
            <select value={target} onChange={(event) => setTarget(event.target.value)} className={field}>
              {CALENDAR_TARGETS[scope].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className={labelCls}>From</span>
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className={field} />
          </label>
          <label>
            <span className={labelCls}>To</span>
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className={field} />
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Type</span>
            <select value={blockType} onChange={(event) => setBlockType(event.target.value)} className={field}>
              {BLOCK_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Reason (optional)</span>
            <input
              type="text"
              value={reason}
              maxLength={120}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Irrigation channel repair"
              className={field}
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-foreground-700 px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
          >
            <i className={saving ? "ri-loader-4-line animate-spin" : "ri-calendar-close-line"}></i>
            {saving ? "Saving…" : "Block dates"}
          </button>
        </div>
      </div>
    </div>
  );
}